import { useEffect, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import Button from 'components/lib/Button';
import Text from 'components/lib/Text';
import { useGivingFormData, useQueryParam } from 'hooks';
import { Designation, DesignationInstance, SelectedDesignation } from 'types';
import { IDonateQueryParam } from 'types/QueryParams';
import { usdFormatter } from 'utils/formatters';
import { parseAvailableDesignationQueryParams } from 'utils/queryParamUtils';
import './AdvancedDesignationPicker.scss';
import {
  AdvancedDesignationError,
  AdvancedDesignationRow
} from './AdvancedDesignationRow';

interface AdvancedDesignationPickerProps {
  ungroupedDesignationIds: string[];
  designationGroupIds: string[];

  defaultDesignationId?: string;
  promptText?: string;
  singleDesignation?: boolean;
  remountGiftOptions?: () => void;
}

export const AdvancedDesignationPicker = ({
  ungroupedDesignationIds,
  designationGroupIds,
  defaultDesignationId,
  promptText,
  singleDesignation,
  remountGiftOptions
}: AdvancedDesignationPickerProps) => {
  const { control, getValues, watch, setValue } = useFormContext();
  const { campaignDesignations, campaignDesignationGroups } =
    useGivingFormData();
  const designationQueryParam = useQueryParam(IDonateQueryParam.Designation);
  const designationsQueryParam = useQueryParam(IDonateQueryParam.Designations);
  const parsedDesignationsFromQuery = parseAvailableDesignationQueryParams(
    designationsQueryParam,
    campaignDesignations
  );
  const [giftOptionsAreHidden, setGiftOptionsAreHidden] = useState<boolean>(
    !!parsedDesignationsFromQuery.length
  );
  const giftAmount = watch('giftAmount');

  const availableDesignationGroups = useMemo(
    () =>
      campaignDesignationGroups.filter(({ id }) =>
        designationGroupIds.includes(id)
      ),
    [designationGroupIds, campaignDesignationGroups]
  );

  const prePopulatedDesignations: DesignationInstance[] = useMemo(() => {
    const result: DesignationInstance[] = [];
    if (parsedDesignationsFromQuery) {
      result.push(...parsedDesignationsFromQuery);
    }

    if (!result.length && designationQueryParam) {
      const queryDesignation = campaignDesignations.find(
        ({ id }) => id === designationQueryParam
      );

      if (queryDesignation) {
        result.push({
          key: uuid(),
          amount: giftAmount,
          ...queryDesignation
        });
      }
    }

    // Calculate total available designations (ungrouped + grouped)
    const totalAvailableDesignations =
      ungroupedDesignationIds.length +
      campaignDesignationGroups
        .filter((group) => designationGroupIds.includes(group.id))
        .reduce((total, group) => total + group.designations.length, 0);

    if (
      !result.length &&
      (defaultDesignationId || totalAvailableDesignations === 1)
    ) {
      let foundDesignation;

      if (defaultDesignationId) {
        // Search through all campaign designations to find the default designation
        foundDesignation = campaignDesignations.find(
          ({ id }) => id === defaultDesignationId
        );

        // If not found in campaign designations, search through designation groups
        if (!foundDesignation) {
          campaignDesignationGroups.some((group) => {
            foundDesignation = group.designations.find(
              ({ id }) => id === defaultDesignationId
            );
            if (foundDesignation) {
              return true; // break out of some()
            }
            return false;
          });
        }
      } else if (totalAvailableDesignations === 1) {
        // If only one designation available, use it (either ungrouped or from a group)
        if (ungroupedDesignationIds.length === 1) {
          foundDesignation = campaignDesignations.find(
            ({ id }) => id === ungroupedDesignationIds[0]
          );
        } else {
          // Must be one designation in a group
          const groupWithSingleDesignation = campaignDesignationGroups
            .filter((group) => designationGroupIds.includes(group.id))
            .find((group) => group.designations.length === 1);
          if (groupWithSingleDesignation) {
            [foundDesignation] = groupWithSingleDesignation.designations;
          }
        }
      }

      if (foundDesignation) {
        result.push({
          key: uuid(),
          amount: giftAmount,
          ...foundDesignation
        });
      }
    }

    return result;
  }, [
    defaultDesignationId,
    designationQueryParam,
    ungroupedDesignationIds,
    designationGroupIds,
    campaignDesignations,
    campaignDesignationGroups,
    giftAmount,
    parsedDesignationsFromQuery
  ]);

  const availableDesignations = useMemo(
    () =>
      campaignDesignations.filter(({ id }) =>
        ungroupedDesignationIds.includes(id)
      ),
    [ungroupedDesignationIds, campaignDesignations]
  );

  const {
    field: { value, onChange },
    fieldState: { error: fieldError }
  } = useController({
    name: 'designation',
    control,
    defaultValue: prePopulatedDesignations.length
      ? prePopulatedDesignations
      : [
          {
            key: uuid(),
            amount: 0
          }
        ]
  });

  useEffect(() => {
    // if designations are populated from query params, gift options never mount
    // and initial gift option is never set, preventing form from being submitted
    // setting initial gift amount in that event
    if (parsedDesignationsFromQuery.length > 0) {
      const donationAmountFromParams = parsedDesignationsFromQuery.reduce(
        (acc: number, curr: { amount?: number }) => acc + (curr.amount ?? 0),
        0
      );
      setValue('giftAmount', donationAmountFromParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /** Copied from DesignationPicker.tsx:
     * When giftAmount changes, it means the gift options were visible, which in turn means that
     * we must only have one designation. In this case, we just update the amount on the single
     * designation we have to keep it in sync. This helps us show the text next to the single
     * designation and have it match whatever the selected amount is.
     */
    const currentDesignations = getValues('designation');
    if (giftAmount && currentDesignations?.length <= 1) {
      setValue('designation', [
        {
          ...(currentDesignations?.[0] ?? {}),
          amount: giftAmount ?? 0
        }
      ]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftAmount]);

  // remount gift options when the amount of designations drops back down to one (from multiple)
  useEffect(() => {
    if (value.length === 1) {
      // we only want to remount if we don't have available `designations` query params
      if (parsedDesignationsFromQuery.length === 0) {
        setGiftOptionsAreHidden(false);
        remountGiftOptions?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Combine ungrouped designations with availableDesignationGroups.designations
  const combinedAvailableDesignations = useMemo(
    () =>
      availableDesignations.concat(
        availableDesignationGroups.reduce(
          (previousValue, currentValue) =>
            previousValue.concat(currentValue.designations),
          [] as Designation[]
        )
      ),
    [availableDesignations, availableDesignationGroups]
  );

  /**
   * If there is only one designation option, we don't show the designation picker.
   */
  const totalAvailableDesignations =
    ungroupedDesignationIds.length +
    campaignDesignationGroups
      .filter((group) => designationGroupIds.includes(group.id))
      .reduce((total, group) => total + group.designations.length, 0);

  if (totalAvailableDesignations === 1) return null;

  /**
   * Here we have a list of designation ids that have already been selected. These will be filtered out of the combo box
   * in the designation picker.
   */
  const selectedDesignationIds =
    getValues('designation')?.map(({ id }: SelectedDesignation) => id) ?? [];

  const donationAmount = value.reduce(
    (acc: number, curr: { amount?: number }) => acc + (curr.amount ?? 0),
    0
  );

  /**
   * Errors come back in one of two ways.
   *
   * If the donation total is not in range of their selected minimum and $250,000, the error looks like { type: 'is-in-range', message: string }
   *
   * If the donation total is fine, but maybe they forgot to select a designation or enter an amount for one, then we get back an array []
   * the index of which represent which designation is invalid and whether the title or amount is invalid.
   */

  let errorMessage;

  if (fieldError?.type === 'is-in-range') {
    const { message } = fieldError;
    errorMessage = message;
  }

  const error = fieldError as unknown as AdvancedDesignationError[];

  return (
    <div className="GF-AdvancedDesignationPicker">
      <div className="GF-AdvancedDesignationPicker__Container">
        <div className="GF-AdvancedDesignationPicker__Title">
          <Text variant="h5">{promptText}</Text>
          {giftOptionsAreHidden ||
            (value.length !== 1 && (
              <Text
                variant="h5"
                className="GF-AdvancedDesignationPicker__Amount--Label"
              >
                Amount
              </Text>
            ))}
        </div>
        {value // render one AdvancedDesignationRow for each selected designation
          .slice(0, singleDesignation ? 1 : undefined)
          .map(
            (
              designation: SelectedDesignation,
              i: number,
              arr: SelectedDesignation[]
            ) => (
              <AdvancedDesignationRow
                key={designation.key}
                designation={
                  designation?.title ||
                  designation?.amount !== undefined ||
                  designation?.id
                    ? designation
                    : {}
                }
                onChange={(newData) => {
                  const newValue = [...value];
                  newValue[i] = {
                    ...designation,
                    ...newData
                  };

                  onChange(newValue);
                }}
                onDelete={() => {
                  const newValue = [...value];
                  newValue.splice(i, 1);
                  onChange(newValue);
                }}
                onClear={() => {
                  const { key, amount } = designation;
                  const newValue = [...value];
                  newValue[i] = { key, amount };
                  onChange(newValue);
                }}
                designations={availableDesignations}
                designationGroups={availableDesignationGroups}
                excludeDesignationIds={selectedDesignationIds}
                allowAmountEdit={giftOptionsAreHidden || arr.length !== 1}
                showDelete={arr.length !== 1}
                error={error?.[i]}
              />
            )
          )}
        {errorMessage && (
          <Text variant="h6" className="GF-AdvancedDesignationPicker__Error">
            {errorMessage}
          </Text>
        )}
        {!singleDesignation &&
          selectedDesignationIds.length <
            combinedAvailableDesignations.length && (
            <Button
              className="GF-AdvancedDesignationPicker__AddDesignation"
              name="addDesignation"
              onClick={() => {
                onChange([...value, { key: uuid() }]);
                setGiftOptionsAreHidden(true);
              }}
            >
              + Add New Designation
            </Button>
          )}

        {(value.length > 1 || parsedDesignationsFromQuery.length > 0) && (
          <div className="GF-AdvancedDesignationPicker__DonationAmount">
            <div className="GF-AdvancedDesignationPicker__DonationAmount--Background" />
            <div className="GF-AdvancedDesignationPicker__DonationAmount--Text">
              <Text variant="h8">Donation Amount</Text>
              <Text variant="h8">{usdFormatter.format(donationAmount)}</Text>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
